import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "@/components/AuthProvider";
import { usePermissions } from "@/hooks/useUnifiedAuth";
import { useAnalytics } from "@/hooks/useAnalytics";
import { Card, CardContent } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import {
  FileText,
  Bell,
  Star,
  Users,
  UserCircle,
  Mail,
  Upload,
} from "lucide-react";

const TERMS_ADMIN_USER_IDS = [
  "8ae8de44-7553-41de-9781-c2ce054b2af4", // marianalima@musa.co
  "2b3982c5-6a81-4b00-99ca-92d5aace8a0c", // joana@musa.co
  "40b6edf3-f8cb-4730-9363-7e3ee4956adc", // isabelle.soares@musa.co
];

// UUIDs dos usuários autorizados a acessar a página de envio de e-mail
const SEND_MAIL_AUTHORIZED_USER_IDS = [
  "bc240f47-bf4f-44b6-9e8e-019a686acb4d", // Marcos Ribas
  "40b6edf3-f8cb-4730-9363-7e3ee4956adc", // Isabelle Soares
  "bf2cc762-1c6d-4618-841f-fc2d5f2e4147", // Daniel Rodrigues
];

// UUIDs dos usuários autorizados a acessar a página de atualização em massa
const BULK_UPDATE_AUTHORIZED_USER_IDS = [
  "40b6edf3-f8cb-4730-9363-7e3ee4956adc", // Isabelle Soares
  "bc240f47-bf4f-44b6-9e8e-019a686acb4d", // Marcos Ribas
  "bf2cc762-1c6d-4618-841f-fc2d5f2e4147", // Daniel Rodrigues
];

const supportPages = [
  // Mitigação temporária: Notificações oculto até correção definitiva
  // {
  //   id: "notificacoes",
  //   name: "Notificações",
  //   description: "Alertas e comunicados",
  //   icon: Bell,
  //   path: "/notificacoes",
  //   internalOnly: false,
  //   disabled: false,
  // },
  {
    id: "termos",
    name: "Termos de uso",
    description: "Políticas e condições do portal",
    icon: FileText,
    path: "/termosdeuso",
    internalOnly: false,
    disabled: true,
  },
  {
    id: "csat",
    name: "CSAT",
    description: "Métricas de satisfação do cliente",
    icon: Star,
    path: "/csat",
    internalOnly: true,
    disabled: false,
  },
  {
    id: "sendmail",
    name: "Envio de comunicação via e-mail",
    description: "Envio de e-mails para usuários",
    icon: Mail,
    path: "/send-mail",
    internalOnly: true,
    disabled: false,
    isNew: true,
  },
  {
    id: "bulkupdate",
    name: "Atualização em massa (CSV)",
    description: "Atualize registros do banco via upload de CSV",
    icon: Upload,
    path: "/bulk-update",
    internalOnly: true,
    disabled: false,
    isNew: true,
  },
];

const Configuracoes = () => {
  const { profile, isLoading } = useAuthContext();
  const { canManageUsers } = usePermissions();
  const navigate = useNavigate();
  const { trackButtonClick } = useAnalytics();

  const isInternal = profile?.is_internal === true;
  const isTermsAdmin =
    profile?.user_id && TERMS_ADMIN_USER_IDS.includes(profile.user_id);
  const isSendMailAuthorized =
    profile?.user_id && SEND_MAIL_AUTHORIZED_USER_IDS.includes(profile.user_id);
  const isBulkUpdateAuthorized =
    profile?.user_id &&
    BULK_UPDATE_AUTHORIZED_USER_IDS.includes(profile.user_id);

  const getPagePath = (page: (typeof supportPages)[0]) => {
    if (page.id === "termos") {
      return isTermsAdmin ? "/termosdeusomusa" : "/termosdeuso";
    }
    return page.path;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="container mx-auto py-6 space-y-6">
        <h1 className="text-3xl font-bold text-foreground">Configurações</h1>

        <Card>
          <CardContent className="space-y-3 pt-6 shadow-md">
            {/* Card de Usuários ou Meu Perfil — exclusivos entre si */}
            <div
              data-testid={
                canManageUsers ? "config-usuarios" : "config-meu-perfil"
              }
              className="flex items-center justify-between shadow-sm p-4 rounded-lg border transition-colors bg-neutral-100 hover:bg-neutral-200 cursor-pointer"
              onClick={() => {
                // Os dois ramos são registrados com nomes próprios (GR-712, D-07).
                trackButtonClick({
                  buttonLabel: canManageUsers
                    ? "configuracoes_click_usuarios"
                    : "configuracoes_click_meu_perfil",
                });
                navigate(canManageUsers ? "/usuarios" : "/meu-perfil");
              }}
            >
              <div className="flex items-center gap-3">
                {canManageUsers ? (
                  <Users className="h-5 w-5 text-muted-foreground" />
                ) : (
                  <UserCircle className="h-5 w-5 text-muted-foreground" />
                )}
                <div>
                  <p className="text-sm font-semibold">
                    {canManageUsers ? "Usuários" : "Meu Perfil"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {canManageUsers
                      ? "Gerenciamento de usuários e permissões"
                      : "Visualize e edite suas informações"}
                  </p>
                </div>
              </div>
            </div>

            {supportPages
              .filter((page) => {
                // Filtro padrão: se não é internalOnly ou se o usuário é internal
                if (page.internalOnly && !isInternal) return false;

                // Filtro adicional: página de envio de e-mail requer autorização específica
                if (page.id === "sendmail" && !isSendMailAuthorized)
                  return false;

                // Filtro adicional: página de atualização em massa requer autorização específica
                if (page.id === "bulkupdate" && !isBulkUpdateAuthorized)
                  return false;

                return true;
              })
              .map((page) => {
                const Icon = page.icon;
                const path = getPagePath(page);

                const cardContent = (
                  <div
                    key={page.id}
                    className={`flex items-center justify-between shadow-sm p-4 rounded-lg border transition-colors ${
                      page.disabled
                        ? "bg-neutral-50 opacity-50 cursor-not-allowed"
                        : "bg-neutral-100 hover:bg-neutral-200 cursor-pointer"
                    }`}
                    onClick={() => !page.disabled && navigate(path)}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-semibold flex items-center gap-2">
                          {page.name}
                          {"isNew" in page && page.isNew && (
                            <Badge
                              variant="info"
                              className="text-[10px] px-1.5 py-0.5 font-semibold"
                            >
                              Novo
                            </Badge>
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {page.description}
                        </p>
                      </div>
                    </div>
                  </div>
                );

                if (page.disabled) {
                  return (
                    <Tooltip key={page.id}>
                      <TooltipTrigger asChild>{cardContent}</TooltipTrigger>
                      <TooltipContent>
                        <p>Página em construção</p>
                      </TooltipContent>
                    </Tooltip>
                  );
                }

                return cardContent;
              })}
          </CardContent>
        </Card>
      </div>
    </TooltipProvider>
  );
};

export default Configuracoes;
